import { useEffect, useMemo } from 'react';
import _ from 'lodash';
import { useOnboarding } from '../contexts/onboarding.context';
import { useUser } from '../contexts/user.context';
import { mobilizeiStoresService } from '../services/mobilizei/stores.service';

/**
 * The store's active theme, already broken into the pieces the mock preview
 * reads (see Onboarding/_shared/MockAppHome).
 *
 * The theme comes with the store creation and lives on the onboarding context
 * from then on — the Brand step hands over the personalized one right after
 * saving it — so the screens render already themed. The fetch below is only the
 * fallback for when the context has nothing, e.g. an onboarding resumed on a
 * later step in another browser.
 */
export default function useActiveTheme() {
    const { theme, setTheme } = useOnboarding();
    const { user } = useUser();
    const { activeTheme: themeId, id: storeId, platformId } = _.get(user, 'store', {});

    useEffect(() => {
        if (theme || !platformId || !storeId || !themeId) {
            return;
        }

        let cancelled = false;

        mobilizeiStoresService.getThemeById(platformId, storeId, themeId)
            .then(fetched => {
                if (!cancelled && fetched) {
                    setTheme(fetched);
                }
            })
            // The mock falls back to its own empty state — a preview is never
            // worth blocking the screen for.
            .catch(() => { });

        return () => { cancelled = true }
    }, [platformId, setTheme, storeId, theme, themeId]);

    // Memoized so consumers can depend on the identity of these arrays.
    const themeSettings = useMemo(() => _.get(theme, 'settings', []), [theme]);

    const homeScreenSections = useMemo(() => {
        const homeScreenArea = _.get(theme, 'areas', []).find(area => area.key === 'homeScreen');

        return _.get(homeScreenArea, 'sections', []);
    }, [theme]);

    return { activeTheme: theme, homeScreenSections, themeSettings };
}
